/** Cached waterline sections of the actual resident instanced boulders.
 * Four height slices retain sloping facets as the wash rises. No render-target
 * pass, scene-depth readback, circular proxy, or separately placed obstacle. */
import * as T from 'three';
export const ROCK_SPAN=128, ROCK_RES=256, ROCK_STEP=ROCK_SPAN/ROCK_RES;
export const ROCK_LEVELS=[-.2,.15,.5,.85] as const;
export const ROCK_DISTANCE_MAX=4;
const REACH=ROCK_DISTANCE_MAX, N=ROCK_RES+1;
type Point={x:number;y:number};
type Vertex=Point & {h:number};
const cross=(a:Point,b:Point,c:Point)=>(b.x-a.x)*(c.y-a.y)-(b.y-a.y)*(c.x-a.x);
function hull(points:Point[]):Point[]{
  points.sort((a,b)=>a.x-b.x||a.y-b.y);
  const p=points.filter((v,i)=>!i||Math.hypot(v.x-points[i-1].x,v.y-points[i-1].y)>1e-6);
  if(p.length<3)return [];
  const low:Point[]=[],high:Point[]=[];
  for(const v of p){while(low.length>1&&cross(low.at(-2)!,low.at(-1)!,v)<=1e-9)low.pop();low.push(v);}
  for(let i=p.length-1;i>=0;i--){const v=p[i];while(high.length>1&&cross(high.at(-2)!,high.at(-1)!,v)<=1e-9)high.pop();high.push(v);}
  low.pop();high.pop();return low.concat(high);
}
/** Convex rock geometry is generated by intersecting fracture planes. Slice
 * its transformed triangles, then remove duplicate/interior section points. */
export function rockSection(vertices:readonly Vertex[],height:number):Point[]{
  const points:Point[]=[];
  for(let i=0;i<vertices.length;i+=3)for(let j=0;j<3;j++){
    const a=vertices[i+j],b=vertices[i+(j+1)%3];
    if(Math.abs(a.h-height)<1e-7)points.push({x:a.x,y:a.y});
    if((a.h<height&&b.h>height)||(a.h>height&&b.h<height)){
      const t=(height-a.h)/(b.h-a.h);points.push({x:a.x+(b.x-a.x)*t,y:a.y+(b.y-a.y)*t});
    }
  }
  return hull(points);
}
export function sectionDistance(p:Point,polygon:readonly Point[]):number {
  if(polygon.length<3)return REACH;
  let d2=Infinity,inside=true;
  for(let i=0;i<polygon.length;i++){
    const a=polygon[i],b=polygon[(i+1)%polygon.length],x=b.x-a.x,y=b.y-a.y;
    const t=Math.max(0,Math.min(1,((p.x-a.x)*x+(p.y-a.y)*y)/(x*x+y*y)));
    const dx=p.x-a.x-t*x,dy=p.y-a.y-t*y;
    d2=Math.min(d2,dx*dx+dy*dy);
    if(cross(a,b,p)<-1e-8)inside=false;
  }
  return (inside?-1:1)*Math.sqrt(d2);
}

export class ShoreRockField {
  readonly data=new Uint16Array(N*N*4);
  readonly texture=new T.DataTexture(this.data,N,N,T.RGBAFormat,T.HalfFloatType);
  readonly center=new T.Vector2();
  readonly stats={bakes:0,meshes:0,rocks:0,sections:0,milliseconds:0};
  private readonly distances=new Float32Array(N*N*4);
  private key='';
  private readonly matrix=new T.Matrix4();
  private readonly v=new T.Vector3();
  constructor(){
    this.texture.minFilter=this.texture.magFilter=T.LinearFilter;
    this.texture.generateMipmaps=false;
    this.data.fill(T.DataUtils.toHalfFloat(REACH));this.texture.needsUpdate=true;
  }
  update(meshes:ReadonlySet<T.InstancedMesh>,ref:T.Vector3,e:T.Vector3,n:T.Vector3,seaR:number,x:number,y:number,reset=false):void {
    // Visibility participates in invalidation: retired/hidden LODs cannot leave
    // ghost foam. Registration is bounded to coastal tiles, not the whole scene.
    const visible=[...meshes].filter(m=>m.visible);
    const key=`${x},${y}:`+visible.map(m=>m.id).join(',');
    if(!reset&&key===this.key)return;
    const start=performance.now();this.key=key;this.center.set(x,y);
    this.distances.fill(REACH);let rocks=0,sections=0;
    for(const mesh of visible){
      const attr=mesh.geometry.getAttribute('position'),index=mesh.geometry.index;
      for(let i=0;i<mesh.count;i++){
        mesh.getMatrixAt(i,this.matrix);this.v.setFromMatrixPosition(this.matrix);
        const denom=this.v.dot(ref);if(denom<=0)continue;
        const px=seaR*this.v.dot(e)/denom,py=seaR*this.v.dot(n)/denom;
        const rock=mesh.userData.rockEvidence[i];
        if(Math.abs(px-x)>ROCK_SPAN/2+rock.a+REACH||Math.abs(py-y)>ROCK_SPAN/2+rock.a+REACH
          ||Math.abs(this.v.length()-seaR)>rock.a+1)continue;
        const bb=mesh.geometry.boundingBox!,m=this.matrix.elements;
        const sy=Math.hypot(m[4],m[5],m[6]),height=this.v.length()-seaR;
        if(height+bb.min.y*sy>ROCK_LEVELS[3]||height+bb.max.y*sy+rock.a*rock.a/seaR<ROCK_LEVELS[0])continue;
        const vertices:Vertex[]=[];
        for(let j=0;j<(index?.count??attr.count);j++){
          this.v.fromBufferAttribute(attr,index?index.getX(j):j).applyMatrix4(this.matrix);
          const k=seaR/this.v.dot(ref);
          vertices.push({x:this.v.dot(e)*k,y:this.v.dot(n)*k,h:this.v.length()-seaR});
        }
        let intersects=false;
        for(let layer=0;layer<4;layer++){
          const polygon=rockSection(vertices,ROCK_LEVELS[layer]);if(polygon.length<3)continue;
          const xs=polygon.map(p=>p.x),ys=polygon.map(p=>p.y),left=x-ROCK_SPAN/2,bottom=y-ROCK_SPAN/2;
          const minI=Math.max(0,Math.floor((Math.min(...xs)-REACH-left)/ROCK_STEP));
          const maxI=Math.min(ROCK_RES,Math.ceil((Math.max(...xs)+REACH-left)/ROCK_STEP));
          const minJ=Math.max(0,Math.floor((Math.min(...ys)-REACH-bottom)/ROCK_STEP));
          const maxJ=Math.min(ROCK_RES,Math.ceil((Math.max(...ys)+REACH-bottom)/ROCK_STEP));
          for(let j=minJ;j<=maxJ;j++)for(let i=minI;i<=maxI;i++){
            const d=sectionDistance({x:left+i*ROCK_STEP,y:bottom+j*ROCK_STEP},polygon),a=(j*N+i)*4+layer;
            this.distances[a]=Math.min(this.distances[a],d);
          }
          sections++;intersects=true;
        }
        if(intersects)rocks++;
      }
    }
    for(let i=0;i<this.data.length;i++)this.data[i]=T.DataUtils.toHalfFloat(this.distances[i]);
    this.texture.needsUpdate=true;
    Object.assign(this.stats,{bakes:this.stats.bakes+1,meshes:visible.length,rocks,sections,milliseconds:performance.now()-start});
  }
  dispose():void{this.texture.dispose();}
}

export const SHORE_ROCK_GLSL=/* glsl */ `
uniform sampler2D uShoreRocks;
uniform vec2 uRockCenter;
uniform float uRockReady;
float rockDistance(vec2 p,float height){
  vec4 d=texture2D(uShoreRocks,(p-uRockCenter)/128.5+0.5);
  float h=clamp((height+0.2)/0.35,0.0,3.0);
  return h<1.0?mix(d.r,d.g,h):h<2.0?mix(d.g,d.b,h-1.0):mix(d.b,d.a,h-2.0);
}
// Distance, seaward-facing contact, sheltered wake, bend/delay around sides.
vec4 rockResponse(vec2 p,vec2 gradient,float height){
  float weight=uRockReady*(1.0-smoothstep(44.0,56.0,max(abs(p.x-uShoreView.x),abs(p.y-uShoreView.y))));
  if(weight<0.001)return vec4(8.0,0.0,0.0,0.0);
  vec2 sea=gradient/max(length(gradient),0.001);
  float d=rockDistance(p,height);
  vec2 normal=vec2(rockDistance(p+vec2(0.5,0.0),height)-rockDistance(p-vec2(0.5,0.0),height),
    rockDistance(p+vec2(0.0,0.5),height)-rockDistance(p-vec2(0.0,0.5),height));
  normal/=max(length(normal),0.001);
  float facing=dot(normal,sea);
  float wake=max((1.0-smoothstep(-0.3,0.7,rockDistance(p+sea*2.0,height))),
    (1.0-smoothstep(-0.3,0.7,rockDistance(p+sea*5.0,height)))*0.65)*weight;
  float bend=(1.0-smoothstep(0.0,3.5,d))*(1.0-smoothstep(-0.2,0.8,facing))*weight;
  return vec4(mix(8.0,d,weight),smoothstep(-0.45,0.8,facing)*weight,wake,bend);
}
`;
